<template>
  <div>
    <div v-if="busy" class="d-flex align-items-center justify-content-center my-2">
      <spinner :display-loading="true" />
    </div>
    <ul v-else class="list-unstyled">
      <b-card v-for="item in feedback" :key="item.id" class="bg-white mb-4 p-4 shadow border rounded">
        <div class="row fadein-details">
          <div class="col col-md-2 order-0 order-md-0 d-none d-flex flex-wrap justify-content-between">
            <div class="font-size-14 line-height-5 mb-0 d-flex flex-column font-weight-bold">
              <div :id="`tooltip-review-status-${item.id}`">
                <div class="icon-pointer">
                  {{ item.isPublished ? $t('Published') : $t('Not Published') }}
                  <font-awesome-icon icon="info-circle" />
                </div>

                <b-tooltip v-if="item.isPublished" :target="`tooltip-review-status-${item.id}`" triggers="hover">
                  {{ $t('feedbackPosted') }}
                </b-tooltip>
                <b-tooltip v-else :target="`tooltip-review-status-${item.id}`" triggers="hover">
                  {{ $t('feedbackNotPosted') }}
                </b-tooltip>

                <div>{{ item.createdDate | parseDate }}</div>
                <div class="d-block d-md-none">
                  {{ item.customer }}
                </div>
                <div>{{ $t('Lead type:', {workTypeHeading: item.workTypeHeading}) }}</div>
              </div>

              <router-link
                v-if="!isMobile"
                :to="{name: 'my-lead', params: {code: item.code}}"
                class="btn btn-sm btn-primary btn-lead-view align-self-center lead__details-anchor"
              >
                {{ $t('View lead') }}
              </router-link>
            </div>
          </div>
          <div class="col-12 col-md-10 d-md-block order-2 order-md-2 feedback__published">
            <div class="row">
              <div class="col-12 col-md-7 d-flex flex-column col-md order-2 order-md-3">
                <div class="align-items-center customer__feedback d-flex flex-row flex-wrap customer__feedback">
                  <b-form-rating
                    v-if="!isMobile"
                    v-model="item.rating"
                    class="b-rating-feedback"
                    no-border
                    precision="2"
                    readonly
                  />
                  <h2 class="d-none font-size-14 mb-0 ml-md-2 font-weight-700 d-md-block feedback__customer">
                    {{ item.customer }}
                  </h2>
                </div>
                <p v-if="item.comment !== null" class="font-size-14 py-md-3 pt-3 font-weight-normal line-height-6 feedback__comment mb-0">
                  {{ item.comment }}
                </p>
                <p v-else class="text-muted font-size-14 py-md-3 pt-3 font-weight-normal line-height-6 feedback__comment mb-0">
                  {{ $t('The reviewer did not leave any comments') }}
                </p>

                <b-button
                  :id="`share-widget-btn-${item.id}`"
                  size="sm"
                  :class="'sharing-widget align-self-center align-self-md-end'"
                  variant="outline-primary"
                >
                  <font-awesome-icon icon="share-alt" class="mr-2" />
                  {{ $t('Share Review') }}
                </b-button>

                <b-popover :target="`share-widget-btn-${item.id}`" triggers="click" placement="auto">
                  <a
                    class="mr-3"
                    :href="getFacebookUrl(item)"
                    target="_BLANK"
                  >
                    <font-awesome-icon :icon="['fab','facebook']" />
                    {{ $t('Facebook') }}
                  </a>

                  <a
                    v-if="item.comment !== null" class="twitter-share-button"
                    target="_BLANK"
                    :href="getTwitterUrl(item)"
                  >
                    <font-awesome-icon :icon="['fab','twitter']" />
                    {{ $t('Twitter') }}
                  </a>
                  <a v-else class="twitter-share-button"
                     target="_BLANK"
                     :href="getTwitterShareUrl()"
                  >
                    <font-awesome-icon :icon="['fab','twitter']" />
                    {{ $t('Twitter') }}
                  </a>
                </b-popover>
              </div>
              <div class="font-size-14 mb-0 col-12 col-md-5 order-3 order-md-4 response">
                <div v-if="canEdit(item.responseEditUntil)">
                  <validation-observer v-slot="{validate, dirty, reset, valid, invalid}">
                    <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required|length:500">
                      <h2 class="font-size-14">
                        <font-awesome-icon :icon="['fas','reply']" rotation="180" />
                        {{ item.responseEditUntil > (new Date()) ? $t('Reply submitted') : $t('Reply to this review') }}
                        <span v-if="dirty && item.responseEditUntil > (new Date())" class="text-danger">{{ $t('Editing...') }}</span>
                      </h2>
                      <b-form-textarea
                        v-model="item.response"
                        :rows="feedbackTextAreaRows"
                        :readonly="item.readOnly"
                        class="border text-break font-size-14 p-3 mb-2"
                        :placeholder="$t('Enter your response here')"
                        :state="(dirty || validated) ? (valid ? null : false) : null"
                      />
                      <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </validation-provider>
                    <div class="row">
                      <div class="col-12">
                        <div :class="['d-flex', 'justify-content-end', 'flex-row', 'py-2', {'px-2 response-edit rounded border': item.responseEditUntil > (new Date())}]">
                          <div v-if="item.responseEditUntil > (new Date())" class="w-60 font-weight-600 d-flex align-items-center font-size-13">
                            {{ $t('timeLeftToEdit', {timeLeftToEdit: timeLeftToEdit(item.responseEditUntil)}) }}
                          </div>
                          <div class="w-40 d-flex justify-content-end">
                            <b-button
                              v-if="!(item.responseEditUntil > (new Date()))"
                              class="btn-reply align-self-center"
                              size="sm"
                              :disabled="item.busy"
                              @click="onSubmitResponse(item.id, item.response, validate, valid, reset)"
                            >
                              {{ $t('Submit reply') }}
                            </b-button>
                            <b-button
                              v-else
                              class="btn-reply align-self-center"
                              size="sm"
                              :disabled="item.busy"
                              @click="editSubmittedResponse(item.id, item.response, validate, reset, dirty, invalid)"
                            >
                              {{ item.readOnly ? $t('Edit Reply') : $t('Submit reply') }}
                            </b-button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </validation-observer>
                </div>
                <div v-else class="d-flex flex-column flex-wrap">
                  <h2 class="font-size-14">
                    <font-awesome-icon :icon="['fas','reply']" rotation="180" />{{ $t('dateReplySubmitted', {dateSubmitted: $d(item.responseEditUntil, 'short')}) }}
                  </h2>
                  <div class="border responded__container">
                    <div class="p-3 w-100 col-12 col-md responded__message">
                      <div>{{ item.responseEditUntil | parseDate }}</div>
                      <p class="text-break">
                        {{ item.response }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="isMobile" class="col-6 col-sm-4 col-md-3 order-1 order-md-2 d-flex flex-column lead__details">
            <div
              v-if="item.rating"
              class="d-md-none d-block customer__feedback text-right text-md-left"
            >
              <b-form-rating
                v-model="item.rating"
                class="b-rating-feedback mt-0 mb-2 mb-md-0 p-0 h-auto"
                size="lg"
                no-border
                precision="2"
                readonly
              />
            </div>

            <router-link
              :to="{name: 'my-lead', params: {code: item.code}}"
              class="btn btn-sm btn-primary btn-lead-view align-self-center lead__details-anchor"
            >
              {{ $t('View lead') }}
            </router-link>
          </div>
        </div>
      </b-card>
    </ul>
    <b-pagination
      v-if="items/pageSize > 1"
      v-model="page"
      :total-rows="items"
      :per-page="pageSize"
      aria-controls="my-leads-table"
      align="right"
    />
  </div>
</template>

<script>
import Spinner from "@/components/animation/Spinner";
import {ValidationObserver, ValidationProvider} from 'vee-validate';
import userDevice from "@/imports/mixins/userDevice";
import notification from "@/imports/mixins/notification";
import dates from "@/services/date";

export default {
  name: "FeedbackList",
  components: {Spinner, ValidationObserver, ValidationProvider},
  mixins: [userDevice, notification],
  props: ['sort', 'filter'],
  data() {
    return {
      busy: false,
      page: 1,
      items: 0,
      pageSize: 10,
      listingUrl: '',
      feedback: [],
    };
  },
  computed: {
    feedbackTextAreaRows: function () {
      return this.isMobile ? 2 : 3;
    }
  },
  methods: {
    loadFeedback: function() {
      this.busy = true;
      this.feedback = [];
      this.$api.feedback.list({...this.filter, page: this.page, order: this.sort}).then((result) => {
        this.busy = false;
        this.setMeta(result.data.meta);
        this.feedback = result.data.data.map((feedback) => {
          feedback.readOnly = false;
          if (feedback.responseEditUntil !== null) {
            feedback.responseEditUntil = new Date(feedback.responseEditUntil);
            feedback.readOnly = true;
          }
          feedback.rating = feedback.rating / 2;
          feedback.busy = false;
          return feedback;
        });
      });
      this.$api.company.getLiveListings().then(({data}) => {
        if (data.data.listings['boilerguide.co.uk']) {
          this.listingUrl = 'boilerguide.co.uk';
        } else if (data.data.listings['solarguide.co.uk']) {
          this.listingUrl = 'solarguide.co.uk';
        } else {
          this.listingUrl = 'https://www.boilerguide.co.uk/'
        }
      });
    },
    setMeta: function(metaData) {
      this.items = metaData.totalResults;
      this.pageSize = metaData.pageSize;
    },
    parseDate: function(datetime) {
      let date = new Date(datetime);
      return date.toLocaleString(['en-GB']);
    },
    onSubmitResponse: function(id, response, validate, valid, reset) {
      // eslint-disable-next-line no-param-reassign
      response = response ?? '';
      let index = null;
      for (const i in this.feedback) {
        if (this.feedback[i].id === id) {
          index = i;
        }
      }
      this.validateResponse(id, index, response, validate, reset, false);
    },
    editSubmittedResponse: function(id, response, validate, reset, dirty, invalid) {
      let index = null;
      for (const i in this.feedback) {
        if (this.feedback[i].id === id) {
          index = i;
        }
      }

      if (invalid) {
        this.feedback[index].readOnly = false;
      } else {
        this.feedback[index].readOnly = dirty;
      }

      if (this.feedback[index].readOnly) {
        this.validateResponse(id, index, response, validate, reset, true);
      }
    },
    validateResponse: function(id, index, response, validate, reset, isEdit) {
      validate().then((success) => {
        if (success) {
          this.feedback[index].busy = true;
          if (!isEdit) {
            this.feedback[index].readOnly = true;
          }
          this.$api.feedback.respond(id, response).then(({data}) => {
            this.feedback[index].responseEditUntil = new Date(data.data.responseEditUntil);
            this.notify(
              this.$bvToast,
              this.$t('Your reply has been', {updatedOrSent: isEdit ? this.$t('updated') : this.$t('sent') }),
              null,
              'success'
            );
          }).finally(() => {
            this.feedback[index].busy = false;
            reset();
          });
        }
      });
    },
    canEdit: function(ts) {
      return (ts === null) || (ts > Date.now());
    },
    timeLeftToEdit: function(ts) {
      return dates.timeToNow(ts)
    },
    getFacebookUrl: function(item) {
      return `https://www.facebook.com/dialog/share?app_id=1709151239250511&href=${this.listingUrl}${item.comment ? '&quote=' + item.comment : ''}`
    },
    getTwitterUrl: function(item) {
      return `https://twitter.com/intent/tweet?text=Another%20Great%20Review%20on%20Boiler%20Guide!%20%22${item.comment.slice(0, 120)}...%22&url=${this.listingUrl}`
    },
    getTwitterShareUrl: function() {
      return `https://twitter.com/intent/tweet?text=Another%20Great%20Review%20on%20Boiler%20Guide!%20%22...%22&url=${this.listingUrl}`
    }
  },
  watch: {
    sort: function() {
      this.loadFeedback();
    },
    filter: function() {
      this.loadFeedback();
    },
    page: function() {
      this.loadFeedback();
    },
  },
  created() {
    this.loadFeedback();
  },
}
</script>

<style scoped lang="scss">
  .btn-reply {
    width: 110px;
  }
  .responded {
    &__container {
      background: $feedback-message-textarea-bg;
      height: 8rem;
    }

    &__message {
      height: 6.825rem;
      overflow-y: auto;
    }
  }

  .responded__container {
    border-radius: $border-radius-sm;
  }

  .lead__details-anchor,
  .sharing-widget {
    margin-top: auto;
  }

  .sharing-widget {
  width: 145px;
  }

  ::v-deep .customer__feedback {
    .b-rating-feedback {
      font-size: 1.313rem;
      padding: 0;
      max-width: 125px;
      height: auto;

      .b-rating-star {
        padding: 0 .1rem;
        color: $theme-rating-color;
      }
    }
  }

  .feedback__customer {
    line-height: 18px;
  }

  .feedback__comment {
    white-space: pre-wrap;
  }

  @include media-breakpoint-down(sm) {
    .page-container {
      .feedback__container {
        padding: 0;
      }
    }

    .lead__details {
      max-width: 150px;
    }

    .sharing-widget {
      margin-top: 1.5rem;
      margin-bottom: 1.5rem;
    }
  }

  .response {
    @include media-breakpoint-up(md) {
      &:before {
        content: '';
        height: 100%;
        position: absolute;
        width: 3px;
        background: #fef3cc;
        left: 0;
      }
    }

    .form-control {
      font-size: $font-size-14;
    }

    &-edit {
      background: #fef3cc
    }
  }
</style>
